import React, { useState, useEffect, useCallback } from 'react';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { Loader2, Clock, User, MapPin } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { toast } from 'sonner';

const ACTION_LABEL = {
  created: { label: 'Created', color: 'bg-emerald-500/10 text-emerald-700 border-emerald-500/30' },
  updated: { label: 'Updated', color: 'bg-blue-500/10 text-blue-700 border-blue-500/30' },
  status_changed: { label: 'Status', color: 'bg-purple-500/10 text-purple-700 border-purple-500/30' },
  archived: { label: 'Archived', color: 'bg-red-500/10 text-red-700 border-red-500/30' },
};

/**
 * Cross-unit / cross-venue audit history feed.
 * Consumes GET /api/inventory/history (aggregate).
 */
export const HistoryTimelineView = ({ api, venueId }) => {
  const [entries, setEntries] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 300 };
      if (venueId) params.venue_id = venueId;
      const res = await api.get('/inventory/history', { params });
      setEntries(res.data.entries || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load history');
    } finally {
      setLoading(false);
    }
  }, [api, venueId]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return <div className="py-8 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>;
  }
  if (entries.length === 0) {
    return <Card className="p-8 text-center text-sm text-muted-foreground">No history entries yet.</Card>;
  }

  return (
    <div className="space-y-3" data-testid="history-timeline">
      <p className="text-xs text-muted-foreground">
        Showing latest {entries.length} of {total} events{venueId ? ' at this centre' : ' across all centres'}
      </p>
      <div className="space-y-2">
        {entries.map((e, i) => {
          const meta = ACTION_LABEL[e.action] || { label: e.action, color: 'bg-muted/40 text-muted-foreground border-border' };
          let when = '';
          try {
            when = e.changed_at ? format(parseISO(e.changed_at), 'PPp') : '';
          } catch { when = e.changed_at?.slice(0, 19) || ''; }
          return (
            <Card key={i} className="p-3" data-testid={`history-entry-${i}`}>
              <div className="flex items-start gap-3">
                <Badge className={`shrink-0 text-[10px] ${meta.color}`}>{meta.label}</Badge>
                <div className="flex-1 min-w-0 text-xs space-y-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <b className="text-sm">{e.unit_name}</b>
                    {e.unit_archived && <Badge variant="outline" className="text-[9px]">archived</Badge>}
                    {e.venue_name && (
                      <span className="text-muted-foreground inline-flex items-center gap-1">
                        <MapPin className="w-3 h-3" /> {e.venue_name}
                      </span>
                    )}
                  </div>
                  {e.action === 'status_changed' && (
                    <p>
                      Status: <b>{e.from_status}</b> → <b>{e.to_status}</b>
                      {e.client_name && <> · Client: <b>{e.client_name}</b></>}
                      {e.lockin_expiry_date && <> · Lock-in: <b>{e.lockin_expiry_date}</b></>}
                    </p>
                  )}
                  {e.note && <p className="text-muted-foreground italic">{e.note}</p>}
                  <p className="text-muted-foreground">
                    <User className="w-3 h-3 inline mr-1" /> {e.changed_by_name}
                    <Clock className="w-3 h-3 inline ml-2 mr-1" /> {when}
                  </p>
                </div>
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
};
